/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import {
  getDeviceIdentifierFromType,
  isMDTDevice as isValidMDTDevice
} from '@ion/core/src/utils/DeviceUtils';
import {isSelectedDeviceSCI} from '@ion/dls/src/editors/DeviceEditorUtils';
import {useCurrentFileHistory} from '@ion/query/src/hooks/useCurrentFileHistory';
import {useDeviceManufacturingInfo} from '@ion/query/src/hooks/useDeviceManufacturingInfo';
import {useDeviceShippingInfo} from '@ion/query/src/hooks/useDeviceShippingInfo';
import {useSmartSearchDevice} from '@ion/query/src/hooks/useSmartSearchDevice';
import {showJobCreateEditor} from '../../components/jobs/state';
import {useFilePermissionsAction} from '../../hooks/useFilePermissionsAction';
import {deviceDetailSelector} from '../../store/selectors/devices';
import BulkViewDeviceEditor from './BulkViewDeviceEditor';
import BulkViewDeviceTabs, {getViewDeviceTabs} from './BulkViewDeviceTabs';
import {deviceSwapResultSelector} from './selector';
import {
  auditDevices,
  editAdminDevice,
  moveAdminDevices,
  setDevicesTabs,
  showBulkViewDevices,
  swapAdminDevice,
  swapAdminDeviceConfirm,
  viewDevice
} from './state';
import {adminDevicesEditResultSelector, deviceMoveResultSelector, viewDevicesTabsSelector} from './state-selectors';
import {ViewDeviceEditorProps} from './ViewDeviceEditor';

interface BulkViewDeviceContainerProps extends ViewDeviceEditorProps {
  accountId: number;
};

const useStyles = makeStyles(() => ({
  deviceTabs : {
    marginTop : '15px'
  },
  tabContentContainer : {
    minWidth  : '90%',
    minHeight : '400px'
  }
}));

/**
 * Holds dialog content for Bulk View Device.
 */
export const BulkViewDeviceContainer = ({
  accountId,
  selectedJobId,
  devicesPermissions,
  hasCreateJob,
  Features,
  includeEditAction = false,
  refreshDevice = false,
  explicitReportDate = false,
  isToggleSwitchGrid = false,
  isVbuFwEnabled = false,
  isShowColumnChooserIcon = false,
  isCtcAdmin = false,
  isShowRefreshButton = true
}: BulkViewDeviceContainerProps) => {
  const localClasses     = useStyles();
  const dispatch         = useDispatch();
  const tabItems         = useSelector(viewDevicesTabsSelector);
  const editDeviceResult = useSelector(adminDevicesEditResultSelector);
  const moveDeviceResult = useSelector(deviceMoveResultSelector);
  const swapDeviceResult = useSelector(deviceSwapResultSelector);
  const deviceDetail     = useSelector(deviceDetailSelector);
  const isMDTDevice      = isValidMDTDevice(deviceDetail);
  const deviceIdentifier = getDeviceIdentifierFromType(deviceDetail);
  const {fileHistory}    = useCurrentFileHistory(deviceIdentifier);
  const {
    device: populatedDevice,
    refresh: onRefreshDevice
  } = useSmartSearchDevice({id: deviceDetail?.id});
  const {
    manufacturingInfo,
    refresh: onRefreshManufacturingInfo
  } = useDeviceManufacturingInfo(deviceIdentifier, isMDTDevice, !!deviceIdentifier);
  const {
    shippingInfo,
    refresh: onRefreshShippingInfo
  } = useDeviceShippingInfo(deviceIdentifier, !!deviceIdentifier);

  const onRefresh = useCallback(() => {
    dispatch(viewDevice(populatedDevice, refreshDevice) as any);
    onRefreshDevice();
    onRefreshManufacturingInfo();
    onRefreshShippingInfo();
  }, [populatedDevice, refreshDevice, onRefreshManufacturingInfo, onRefreshShippingInfo]);

  const onEdit = useCallback(() => {
    dispatch(moveAdminDevices(null, false));
    // Pass null since the edit dialog now pulls in the device information itself.
    dispatch(editAdminDevice(null));
  }, []);

  const onCreateJob = useCallback(() => {
    dispatch(showJobCreateEditor([populatedDevice], null, true));
  }, [populatedDevice]);

  const onMoveDevice = useCallback(() => {
    dispatch(moveAdminDevices([populatedDevice]));
  }, [populatedDevice]);

  const onAudit = useCallback(() => {
    dispatch(auditDevices(populatedDevice));
  }, [populatedDevice]);

  const onSwap = useCallback(() => {
    dispatch(moveAdminDevices(null, false));
    if (populatedDevice?.groups?.length || populatedDevice?.asset?.href || isSelectedDeviceSCI(populatedDevice)) {
      dispatch(swapAdminDeviceConfirm(populatedDevice));
    } else {
      dispatch(swapAdminDevice(populatedDevice));
    }
  }, [populatedDevice]);

  const hasFilePermissions = useFilePermissionsAction({Features});

  useEffect(() => {
    const selected = tabItems?.map(({data}) => data);
    if (selected?.length && (editDeviceResult || moveDeviceResult?.length || swapDeviceResult)) {
      const result = editDeviceResult || moveDeviceResult?.[0] || swapDeviceResult;
      const updated = selected.map((device) => {
        if (device?.id === result?.id) {
          device = result;
        }
        return device;
      });
      const items = getViewDeviceTabs(updated);
      dispatch(showBulkViewDevices(updated));
      dispatch(setDevicesTabs(items, result));
    }
  }, [tabItems, editDeviceResult, moveDeviceResult, swapDeviceResult]);

  return (
    <>
      <div className={localClasses.deviceTabs}>
        <BulkViewDeviceTabs onRefresh={onRefresh}
          onEdit={onEdit}
          onSwap={onSwap}
          onAudit={onAudit}
          onCreateJob={onCreateJob}
          onMoveDevice={onMoveDevice}
          populatedDevice={populatedDevice}
          devicesPermissions={devicesPermissions}
          accountId={accountId}
          hasFilePermissions={hasFilePermissions}
          hasCreateJob={hasCreateJob}
          includeEditAction={includeEditAction} />
      </div>
      <div className={localClasses.deviceTabs}>
        <div className={localClasses.tabContentContainer}>
          <BulkViewDeviceEditor onRefresh={onRefresh}
            Features={Features}
            refreshDevice={refreshDevice}
            devicesPermissions={devicesPermissions}
            populatedDevice={populatedDevice}
            currentFileHistory={fileHistory}
            hasCreateJob={hasCreateJob}
            selectedJobId={selectedJobId}
            includeEditAction={includeEditAction}
            isVbuFwEnabled={isVbuFwEnabled}
            explicitReportDate={explicitReportDate}
            isToggleSwitchGrid={isToggleSwitchGrid}
            isShowColumnChooserIcon={isShowColumnChooserIcon}
            isCtcAdmin={isCtcAdmin}
            isShowRefreshButton={isShowRefreshButton}
            manufacturingInfo={manufacturingInfo}
            shippingInfo={shippingInfo} />
        </div>
      </div>
    </>
  );
};

export default memo(BulkViewDeviceContainer);
